import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Forbidden from "@/pages/Forbidden";
import Layout from "@/components/Layout";

/**
 * FASE 7.4 STEP E — Frontend permission guard.
 *
 * Props:
 *   - permission: string        (must-have single permission)
 *   - anyPermissions: string[]  (must-have at least one)
 *
 * FASE 7.4 STEP F.1 — legacy `adminOnly` (role-hardcoded) prop removed.
 * All routes now gate on explicit permissions from the backend registry.
 *
 * Behaviour:
 *   auth loading      → subtle spinner (no premature Forbidden)
 *   unauthenticated   → redirect /login
 *   authenticated + authorized  → children wrapped in Layout
 *   authenticated + not authorized → Forbidden page (also wrapped in Layout)
 */
export default function ProtectedRoute({
  children,
  permission,
  anyPermissions,
}) {
  const { user, can, canAny } = useAuth();

  // 1) still checking cookie/session — no flash
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-sm text-neutral-500">Caricamento…</div>
      </div>
    );
  }

  // 2) anonymous → login
  if (!user) return <Navigate to="/login" replace />;

  // 3) permission check
  let authorized = true;
  if (permission) authorized = authorized && can(permission);
  if (anyPermissions && anyPermissions.length) authorized = authorized && canAny(anyPermissions);

  if (!authorized) {
    // Wrap in Layout so the sidebar remains visible — user can navigate away.
    return (
      <Layout>
        <Forbidden />
      </Layout>
    );
  }

  return children;
}
